import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Clock, RefreshCw, Plus, Edit2, Info, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from './AuthDialog';
import DecisionDialog from './DecisionDialog';
import FeedbackSection from './FeedbackSection';
import DataPromptHelper from './DataPromptHelper';
import NeedsMoreDetailPrompt from './NeedsMoreDetailPrompt';
import LoadingSkeleton from './LoadingSkeleton';
import { DecisionRenderer } from './decision';
import { parseLogicData, parseVibeData, parseUniverseData } from '../utils/parseComparisonData';
import { parseOptionC } from '../utils/parseOptionC';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const UserOrganizedViewPage = ({ user, setUser, mode = 'edit' }) => {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const isReadOnly = mode === 'view';
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [updatingPrompt, setUpdatingPrompt] = useState(false);
  const [title, setTitle] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [savedPrompt, setSavedPrompt] = useState(''); // For "You've asked" display
  const [contextualPrompts, setContextualPrompts] = useState('');
  const [logicPros, setLogicPros] = useState([]);
  const [logicCons, setLogicCons] = useState([]);
  const [vibeInput, setVibeInput] = useState('');
  const [serendipityInput, setSerendipityInput] = useState('');
  const [newPro, setNewPro] = useState('');
  const [newCon, setNewCon] = useState('');
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [optionAIcon, setOptionAIcon] = useState('');
  const [optionBIcon, setOptionBIcon] = useState('');
  const [optionC, setOptionC] = useState(null);
  const [showDataPrompt, setShowDataPrompt] = useState(true);

  // Detect if ALL sections are completely empty
  const isTooSparse = useMemo(() => {
    const logicCount = (logicPros?.length || 0) + (logicCons?.length || 0);
    const vibeHasContent = (vibeInput || '').trim().length > 0;
    const universeHasContent = (serendipityInput || '').trim().length > 0;
    
    // Only show "I'd love to help" if ALL sections are empty
    return logicCount === 0 && !vibeHasContent && !universeHasContent;
  }, [logicPros, logicCons, vibeInput, serendipityInput]);

  // Parse Logic data once and cache it (only when data exists)
  const logicParsed = useMemo(() => {
    if (!logicPros || !logicCons || (logicPros.length === 0 && logicCons.length === 0)) {
      return { optionA: { name: 'Option A', items: [] }, optionB: { name: 'Option B', items: [] } };
    }
    return parseLogicData(logicPros, logicCons);
  }, [logicPros, logicCons]);

  // Parse Universe data once and cache it (using Logic names)
  const universeParsed = useMemo(() => {
    const parsed = parseUniverseData(serendipityInput || '');
    
    // Match Universe option names to Logic option names (don't just blindly override)
    // Check if Universe's optionA matches Logic's optionA or optionB by name similarity
    const universeAName = parsed.optionA.name.toLowerCase();
    const universeBName = parsed.optionB.name.toLowerCase();
    const logicAName = logicParsed.optionA.name.toLowerCase();
    const logicBName = logicParsed.optionB.name.toLowerCase();
    
    // If Universe optionA name is closer to Logic optionB, swap them
    const aMatchesB = universeAName.includes(logicBName.split(' ')[0]) || logicBName.includes(universeAName.split(' ')[0]);
    const bMatchesA = universeBName.includes(logicAName.split(' ')[0]) || logicAName.includes(universeBName.split(' ')[0]);
    
    if (aMatchesB || bMatchesA) {
      // Swap the items and names
      const temp = { ...parsed.optionA };
      parsed.optionA = { ...parsed.optionB };
      parsed.optionB = temp;
    }
    
    // Now apply Logic names for consistency
    parsed.optionA.name = logicParsed.optionA.name;
    parsed.optionB.name = logicParsed.optionB.name;
    
    return parsed;
  }, [serendipityInput, logicParsed]);

  // Parse Vibe data once and cache it (using Logic names)
  const vibeParsed = useMemo(() => {
    const parsed = parseVibeData(vibeInput || '');
    
    // Match Vibe option names to Logic option names (don't just blindly override)
    const vibeAName = parsed.optionA.name.toLowerCase();
    const vibeBName = parsed.optionB.name.toLowerCase();
    const logicAName = logicParsed.optionA.name.toLowerCase();
    const logicBName = logicParsed.optionB.name.toLowerCase();
    
    // If Vibe optionA name is closer to Logic optionB, swap them
    const aMatchesB = vibeAName.includes(logicBName.split(' ')[0]) || logicBName.includes(vibeAName.split(' ')[0]);
    const bMatchesA = vibeBName.includes(logicAName.split(' ')[0]) || logicAName.includes(vibeBName.split(' ')[0]);
    
    if (aMatchesB || bMatchesA) {
      // Swap the items and names
      const temp = { ...parsed.optionA };
      parsed.optionA = { ...parsed.optionB };
      parsed.optionB = temp;
    }
    
    // Now apply Logic names for consistency
    parsed.optionA.name = logicParsed.optionA.name;
    parsed.optionB.name = logicParsed.optionB.name;
    
    return parsed;
  }, [vibeInput, logicParsed]);

  // Detect missing data
  const missingData = useMemo(() => {
    const universeItemCount = universeParsed.optionA.items.length + universeParsed.optionB.items.length;
    const vibeItemCount = vibeParsed.optionA.items.length + vibeParsed.optionB.items.length;
    
    const missingUniverse = universeItemCount <= 2;
    const missingVibe = vibeItemCount < 2;
    
    if (missingUniverse && missingVibe) return 'both';
    if (missingUniverse) return 'universe';
    if (missingVibe) return 'vibe';
    return 'none';
  }, [universeParsed, vibeParsed]);

  useEffect(() => {
    loadDecision();
  }, [decisionId]);

  const loadDecision = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/decisions/${decisionId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDecision(data);
        setTitle(data.title);
        setOriginalPrompt(data.original_prompt);
        setSavedPrompt(data.original_prompt); // Save for display in "You've asked"
        setContextualPrompts(data.contextual_prompts || '');
        setLogicPros(data.logic_pros || []);
        setLogicCons(data.logic_cons || []);
        setVibeInput(data.vibe_input || '');
        setSerendipityInput(data.serendipity_input || '');
        setOptionAIcon(data.option_a_icon || '');
        setOptionBIcon(data.option_b_icon || '');
        
        // Parse Option C from recommendation
        if (data.ai_recommendation) {
          const parsedOptionC = parseOptionC(data.ai_recommendation);
          setOptionC(parsedOptionC);
        }
      }
    } catch (error) {
      console.error('Error loading decision:', error);
      toast.error('Failed to load decision');
    } finally {
      setLoading(false);
    }
  };

  const updateDecision = async (updates) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/decisions/${decisionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updated = await response.json();
        setDecision(updated);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating:', error);
      return false;
    }
  };

  const handleTitleSave = async () => {
    if (await updateDecision({ title })) {
      setEditingTitle(false);
      toast.success('Title updated');
    }
  };

  const handlePromptUpdate = async () => {
    setUpdatingPrompt(true);
    try {
      if (await updateDecision({ original_prompt: originalPrompt })) {
        setEditingPrompt(false);
        setSavedPrompt(originalPrompt); // Update saved prompt to current edited version
        toast.success('Guidance updated');
        await loadDecision();
      }
    } finally {
      setUpdatingPrompt(false);
    }
  };

  const handleRefreshRecommendation = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/decisions/${decisionId}/refresh-recommendation`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDecision({ ...decision, ai_recommendation: data.recommendation });
        toast.success('Recommendation refreshed');
      }
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const addPro = async () => {
    if (!newPro.trim()) return;
    const updatedPros = [...logicPros, newPro.trim()];
    setLogicPros(updatedPros);
    setNewPro('');
    await updateDecision({ logic_pros: updatedPros });
  };

  const addCon = async () => {
    if (!newCon.trim()) return;
    const updatedCons = [...logicCons, newCon.trim()];
    setLogicCons(updatedCons);
    setNewCon('');
    await updateDecision({ logic_cons: updatedCons });
  };

  const removePro = async (index) => {
    const updatedPros = logicPros.filter((_, i) => i !== index);
    setLogicPros(updatedPros);
    await updateDecision({ logic_pros: updatedPros });
  };

  const removeCon = async (index) => {
    const updatedCons = logicCons.filter((_, i) => i !== index);
    setLogicCons(updatedCons);
    await updateDecision({ logic_cons: updatedCons });
  };

  const handleVibeBlur = async () => {
    if (vibeInput !== decision?.vibe_input) {
      await updateDecision({ vibe_input: vibeInput });
    }
  };

  const handleSerendipityBlur = async () => {
    if (serendipityInput !== decision?.serendipity_input) {
      await updateDecision({ serendipity_input: serendipityInput });
    }
  };

  const handleDecisionClick = (decisionType) => {
    if (!user) {
      setSelectedDecision(decisionType);
      setShowAuthDialog(true);
    } else {
      setSelectedDecision(decisionType);
      setShowDecisionDialog(true);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="max-w-5xl mx-auto py-8 fade-in">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-purple-600 mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            {isReadOnly ? 'Make your own decision' : 'Start a new decision'}
          </button>

          <div className="glass-card p-6">
            {editingTitle ? (
              <div className="flex items-center gap-3">
                <input
                  data-testid="title-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input-field flex-1"
                  autoFocus
                />
                <button onClick={handleTitleSave} className="btn-primary" data-testid="save-title-btn">
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <h1 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="decision-title">
                  {title}
                </h1>
                {!isReadOnly && (
                  <button
                    onClick={() => setEditingTitle(true)}
                    className="text-purple-500 hover:text-purple-600"
                    data-testid="edit-title-btn"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Original Prompt */}
        <div className="glass-card p-6 mb-8">
          <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your Original Prompt
          </h2>
          {editingPrompt ? (
            <div>
              <textarea
                data-testid="prompt-textarea"
                value={originalPrompt}
                onChange={(e) => setOriginalPrompt(e.target.value)}
                className="textarea-field"
                rows={4}
              />
              <div className="flex gap-3 mt-3">
                <button 
                  onClick={handlePromptUpdate} 
                  className="btn-primary flex items-center gap-2" 
                  data-testid="update-prompt-btn"
                  disabled={updatingPrompt}
                >
                  {updatingPrompt ? (
                    <>
                      <div className="loading-spinner w-5 h-5"></div>
                      <span>Updating guidance...</span>
                    </>
                  ) : (
                    <span>Update guidance</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setOriginalPrompt(decision.original_prompt);
                    setEditingPrompt(false);
                  }}
                  className="btn-outline"
                  disabled={updatingPrompt}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 whitespace-pre-wrap mb-3" data-testid="original-prompt">{originalPrompt}</p>
              {!isReadOnly && (
                <button
                  onClick={() => setEditingPrompt(true)}
                  className="text-purple-500 hover:text-purple-600 text-sm flex items-center gap-1 px-3 py-1.5 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                  data-testid="edit-prompt-btn"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit prompt
                </button>
              )}
            </div>
          )}
        </div>

        {/* Show "Needs More Detail" prompt if input is too sparse */}
        {isTooSparse ? (
          <NeedsMoreDetailPrompt
            originalPrompt={savedPrompt}
            contextualPrompts={contextualPrompts}
            onEdit={!isReadOnly ? () => {
              setEditingPrompt(true);
              setTimeout(() => {
                const promptSection = document.querySelector('[data-testid="prompt-textarea"]')?.parentElement?.parentElement;
                if (promptSection) {
                  promptSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            } : null}
            readOnly={isReadOnly}
          />
        ) : (
          <DecisionRenderer 
            decision={decision}
            mode="user"
            dataPromptHelper={
              showDataPrompt && !isTooSparse && (
                <DataPromptHelper
                  missingData={missingData}
                  onEdit={!isReadOnly ? () => {
                    setEditingPrompt(true);
                    setShowDataPrompt(false);
                    setTimeout(() => {
                      const promptSection = document.querySelector('[data-testid="prompt-textarea"]')?.parentElement?.parentElement;
                      if (promptSection) {
                        promptSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  } : null}
                  onDismiss={() => setShowDataPrompt(false)}
                  readOnly={isReadOnly}
                />
              )
            }
          />
        )}

        {/* Feedback Section */}
        {/* Decision Buttons - HIDDEN FOR NOW */}
        {false && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8" data-testid="decision-buttons">
            <button
              onClick={() => handleDecisionClick('YES')}
              className="decision-btn-yes"
              data-testid="yes-btn"
            >
              {optionAIcon && <span className="text-2xl">{optionAIcon}</span>}
              <span className="font-bold">{logicParsed.optionA.name}</span>
            </button>
            <button
              onClick={() => handleDecisionClick(optionC ? optionC.name : 'WAIT')}
              className="decision-btn-wait"
              data-testid="wait-btn"
            >
              <Clock className="w-6 h-6" />
              <span className="font-bold">{optionC ? optionC.buttonLabel : 'WAIT'}</span>
            </button>
            <button
              onClick={() => handleDecisionClick('NO')}
              className="decision-btn-no"
              data-testid="no-btn"
            >
              {optionBIcon && <span className="text-2xl">{optionBIcon}</span>}
              <span className="font-bold">{logicParsed.optionB.name}</span>
            </button>
          </div>
        )}

        {/* Feedback Section */}
        {!isReadOnly && (
          <div>
            <FeedbackSection 
              decisionId={decisionId}
              onFeedbackSubmitted={() => console.log('Feedback submitted')}
            />
          </div>
        )}

        {/* Dialogs */}
        {showAuthDialog && (
          <AuthDialog
            onClose={() => setShowAuthDialog(false)}
            onSuccess={(userData) => {
              setUser(userData);
              setShowAuthDialog(false);
              setShowDecisionDialog(true);
            }}
          />
        )}

        {showDecisionDialog && selectedDecision && (
          <DecisionDialog
            decisionType={selectedDecision}
            decisionId={decisionId}
            onClose={() => {
              setShowDecisionDialog(false);
              setSelectedDecision(null);
            }}
            onSuccess={() => {
              toast.success('Decision saved!');
              navigate('/decisions');
            }}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default UserOrganizedViewPage;
